import { ChecklistItemRecord } from "models/view-models/block-configurations/checklist-item-record";
import { useCallback } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import {
    SortItemMovement,
    useSortedRecords,
} from "utilities/hooks/domain/use-sorted-records";
import { useLocalization } from "utilities/hooks/use-localization";
import ChecklistItemService, {
    ChecklistItemPathParams,
    MoveChecklistItemPathParams,
} from "utilities/services/block-configurations/checklistitems/checklist-item-service";
import { ToastManager } from "utilities/toast/toast-manager";

interface UseSortChecklistItems {
    onItemsReordered: (
        updatedItems: ChecklistItemRecord[]
    ) => Promise<number[]>;
    sortedItems: ChecklistItemRecord[];
}

export const useSortChecklistItems = (
    items: ChecklistItemRecord[]
): UseSortChecklistItems => {
    const { t } = useLocalization();
    const { patch: moveApi } = ChecklistItemService.useMove();

    const { records, onRecordsMoved, resetRecordSort } =
        useSortedRecords<ChecklistItemRecord>(items, ChecklistItemRecord);

    const makeMoveApiCall = useCallback(
        async (
            movePathParams: ChecklistItemPathParams,
            move: MoveChecklistItemPathParams
        ): Promise<number[]> => {
            try {
                const moveResponse = await moveApi(movePathParams, move);

                if (moveResponse.result?.hasErrors()) {
                    throw new Error(
                        moveResponse.result?.listErrorMessages().join(", ")
                    );
                }

                if (moveResponse.resultObject == null) {
                    throw new Error("No checklist item IDs received on move.");
                }

                return moveResponse.resultObject[0];
            } catch (error) {
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("moving"),
                        resource: t("checklist-item"),
                    })
                );

                return resetRecordSort();
            }
        },
        [moveApi, resetRecordSort, t]
    );

    const moveItems = useCallback(
        async (moves: MoveChecklistItemPathParams[]): Promise<number[]> => {
            if (CollectionUtils.isEmpty(moves)) {
                return [];
            }

            let itemIds: number[] = [];

            for (const move of moves) {
                itemIds = await makeMoveApiCall({}, move);
            }

            return itemIds;
        },
        [makeMoveApiCall]
    );

    const onItemsReordered = useCallback(
        async (updatedItems: ChecklistItemRecord[]): Promise<number[]> => {
            const diffs = onRecordsMoved(updatedItems);
            const moves = diffsToMoves(diffs);

            return await moveItems(moves);
        },
        [moveItems, onRecordsMoved]
    );

    return {
        onItemsReordered,
        sortedItems: records,
    };
};

const diffsToMoves = (
    diffs: SortItemMovement[]
): MoveChecklistItemPathParams[] => {
    return diffs.map((diff) => diffToMove(diff));
};

const diffToMove = (diff: SortItemMovement): MoveChecklistItemPathParams => ({
    movingChecklistItemId: diff.movingRecordId,
    precedingChecklistItemId: diff.precedingRecordId,
});
